import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    // Nav
    nav_why_now: 'Why Now',
    nav_platform: 'Platform',
    nav_deployment: 'Deployment',
    nav_security: 'Security',
    nav_login: 'Login',
    nav_request_pilot: 'Request Pilot',

    // Hero
    hero_badge: 'Now available for enterprise pilots',
    hero_title_1: 'Control Plane for',
    hero_title_2: 'Enterprise AI Agents',
    hero_title_3: 'and Non-Human',
    hero_title_4: 'Identities',
    hero_subtitle: 'Discover agents, map permissions, detect policy violations and contain risky actions before they become incidents.',
    hero_cta_pilot: 'Request a Pilot',
    hero_cta_explore: 'Explore Platform',
    hero_trust_1: 'Read-only deployment',
    hero_trust_2: 'Customer VPC option',
    hero_trust_3: '2-week pilot',

    // Why Now
    why_now_title: 'The Problem Is Growing',
    why_now_subtitle: 'Traditional IAM was built for humans. Your infrastructure now runs on autonomous agents, service accounts, and automated workflows that no one governs.',
    why_now_gap_title: 'The Gap in Current IAM',
    why_now_items: [
      'AI agents with autonomous decision-making',
      'Service accounts with standing privileges',
      'API keys shared across environments',
      'Bot identities outside identity governance',
    ],
    why_now_stat_1_value: '247',
    why_now_stat_1_label: 'Avg. non-human identities per enterprise',
    why_now_stat_2_value: '68%',
    why_now_stat_2_label: 'Have excessive privileges',
    why_now_stat_3_value: '< 12%',
    why_now_stat_3_label: 'Are actively governed',

    // Platform
    platform_title: 'One Platform. Complete Governance.',
    platform_subtitle: 'From discovery to containment in a single control plane.',
    platform_features: [
      { title: 'Discovery', desc: 'Auto-discover every non-human identity across IdPs, cloud, and agent frameworks.' },
      { title: 'Permission Graph', desc: 'Map Agent to Credential to Tool to Resource. Understand blast radius instantly.' },
      { title: 'Policy Engine', desc: 'Define and enforce RBAC/ABAC policies. Detect violations in real-time.' },
      { title: 'Kill Switch', desc: 'Instantly disable agents, revoke credentials, or block tools. One click, fully audited.' },
    ],

    // Deployment
    deployment_title: 'Deployment That Meets Your Security Bar',
    deployment_subtitle: 'Choose the model that fits your compliance and data sovereignty requirements.',
    deployment_modes: [
      { title: 'SaaS', desc: 'Managed infrastructure. Read-only connectors. Data encrypted at rest.' },
      { title: 'Customer VPC', desc: 'Deploy in your cloud. Data never leaves your perimeter.' },
      { title: 'Air-Gapped', desc: 'Complete isolation. No external connections. Offline updates.' },
    ],

    // Security
    security_title: 'Security Architecture',
    security_subtitle: 'Built for the most security-conscious organizations',
    security_principles: [
      { title: 'Read-Only First', desc: 'Default deployment uses read-only API access. No write permissions to your infrastructure.' },
      { title: 'Customer-Managed Deployment', desc: 'Deploy in your own VPC or on-premise. Complete data sovereignty and control.' },
      { title: 'Signed Artifacts', desc: 'All binaries and container images are signed. SBOM and provenance attestation included.' },
      { title: 'Least Privilege', desc: 'Minimal permissions by design. No broad admin access required for operation.' },
      { title: 'Tenant Isolation', desc: 'Complete data segregation between tenants. No cross-tenant access possible.' },
      { title: 'Full Auditability', desc: 'Every action logged immutably. Export audit trails for compliance review.' },
    ],

    // Built For
    built_for_title: 'Built For Security Leaders',
    built_for_tabs: {
      ciso: 'CISO',
      identity: 'Identity Security',
      cloud: 'Cloud Security',
      platform: 'Platform Security',
    },
    built_for_ciso_title: 'For the CISO',
    built_for_ciso_desc: 'Board-ready visibility into your non-human identity attack surface. Executive dashboard shows posture score, risk trends, and incident status.',
    built_for_ciso_items: ['Security posture trending', 'Risk quantification', 'Compliance reporting'],
    built_for_ciso_preview: 'Executive View',
    built_for_ciso_metric_1: 'Posture Score',
    built_for_ciso_metric_2: 'Critical',
    built_for_identity_title: 'For Identity Security',
    built_for_identity_desc: 'Extend IAM governance to non-human identities. Discover, classify, and manage every service account, bot, and AI agent.',
    built_for_identity_items: ['Automatic NHI classification', 'Owner assignment workflow', 'Access certification'],
    built_for_identity_preview: 'Identity Inventory',
    built_for_cloud_title: 'For Cloud Security',
    built_for_cloud_desc: 'Track service accounts and workload identities across AWS, Azure, and GCP. Detect privilege escalation and cross-account access.',
    built_for_cloud_items: ['Multi-cloud visibility', 'Privilege detection', 'Blast radius analysis'],
    built_for_cloud_preview: 'Cloud Coverage',
    built_for_platform_title: 'For Platform Security',
    built_for_platform_desc: 'Govern AI agent frameworks before they become ungovernable. Control tool usage and maintain complete audit trails.',
    built_for_platform_items: ['Agent framework connectors', 'Tool approval workflow', 'Policy-as-code'],
    built_for_platform_preview: 'Agent Governance',

    // CTA
    cta_title: 'Ready to Govern Your Non-Human Identities?',
    cta_subtitle: 'Deploy Agent Control Plane in your environment. Guided pilot, dedicated engineering support, full security review.',
    cta_pilot: 'Request Pilot Access',
    cta_explore: 'Explore Platform',

    // Footer
    footer_desc: 'Enterprise security for AI agents and non-human identities.',
    footer_product: 'Product',
    footer_company: 'Company',
    footer_legal: 'Legal',
    footer_rights: 'All rights reserved.',

    // Pilot Form
    pilot_title: 'Request a Controlled Pilot',
    pilot_subtitle: 'Deploy Agent Control Plane in your environment within two weeks. Our team will guide you through setup, integration, and first discovery scan.',
    pilot_name: 'Full Name',
    pilot_email: 'Work Email',
    pilot_company: 'Company',
    pilot_role: 'Role / Title',
    pilot_role_placeholder: 'CISO, VP Security, IAM Director...',
    pilot_nhi: 'Non-Human Identities',
    pilot_nhi_placeholder: 'Estimated count',
    pilot_deployment: 'Preferred Deployment',
    pilot_deployment_placeholder: 'Choose deployment model',
    pilot_stack: 'Current Identity Stack',
    pilot_stack_placeholder: 'e.g., Okta + AWS IAM + CyberArk',
    pilot_notes: 'Additional Context',
    pilot_notes_placeholder: 'Any specific use cases, compliance requirements, or questions...',
    pilot_submit: 'Submit Pilot Request',
    pilot_submitting: 'Submitting...',
    pilot_required: 'Please fill in all required fields.',
    pilot_error: 'Something went wrong. Please try again.',
    pilot_optional: 'optional',
    pilot_consent: 'By submitting, you agree to be contacted by our team regarding your pilot request. We do not share your information with third parties.',
    pilot_trust_1: 'Deploy in your VPC',
    pilot_trust_2: 'Read-only by default',
    pilot_trust_3: '2-week guided pilot',

    // Pilot Confirmation
    pilot_confirm_title: 'Pilot Request Received',
    pilot_confirm_thanks: 'Thank you,',
    pilot_confirm_message: 'Our team will review your request and reach out within 24 hours to schedule a technical briefing for',
    pilot_confirm_summary: 'Request Summary',
    pilot_confirm_deployment: 'Deployment',
    pilot_confirm_nhi: 'NHI Scale',
    pilot_confirm_email_sent: 'Confirmation sent to',
    pilot_confirm_home: 'Back to Home',
    pilot_confirm_explore: 'Explore Platform',

    // NHI options
    nhi_options: ['Under 100', '100 - 500', '500 - 1,000', '1,000 - 5,000', '5,000+', 'Not sure yet'],
    deployment_options: [
      { value: 'SaaS', label: 'SaaS (Managed)' },
      { value: 'Customer VPC', label: 'Customer VPC' },
      { value: 'Air-Gapped / On-Premise', label: 'Air-Gapped / On-Premise' },
      { value: 'Not decided yet', label: 'Not decided yet' },
    ],
  },

  es: {
    // Nav
    nav_why_now: 'El Problema',
    nav_platform: 'Plataforma',
    nav_deployment: 'Despliegue',
    nav_security: 'Seguridad',
    nav_login: 'Ingresar',
    nav_request_pilot: 'Solicitar Piloto',

    // Hero
    hero_badge: 'Disponible para pilotos enterprise',
    hero_title_1: 'Control Plane para',
    hero_title_2: 'Agentes de IA',
    hero_title_3: 'e Identidades',
    hero_title_4: 'No Humanas',
    hero_subtitle: 'Descubra agentes, mapee permisos, detecte violaciones de pol\u00edticas y contenga acciones riesgosas antes de que se conviertan en incidentes.',
    hero_cta_pilot: 'Solicitar un Piloto',
    hero_cta_explore: 'Explorar Plataforma',
    hero_trust_1: 'Despliegue read-only',
    hero_trust_2: 'Opci\u00f3n VPC del cliente',
    hero_trust_3: 'Piloto de 2 semanas',

    // Why Now
    why_now_title: 'El Problema Est\u00e1 Creciendo',
    why_now_subtitle: 'El IAM tradicional fue construido para humanos. Su infraestructura ahora opera con agentes aut\u00f3nomos, cuentas de servicio y workflows automatizados que nadie gobierna.',
    why_now_gap_title: 'La Brecha en el IAM Actual',
    why_now_items: [
      'Agentes de IA con toma de decisiones aut\u00f3noma',
      'Cuentas de servicio con privilegios permanentes',
      'API keys compartidas entre entornos',
      'Identidades bot fuera del gobierno de identidad',
    ],
    why_now_stat_1_value: '247',
    why_now_stat_1_label: 'Identidades no humanas promedio por empresa',
    why_now_stat_2_value: '68%',
    why_now_stat_2_label: 'Tienen privilegios excesivos',
    why_now_stat_3_value: '< 12%',
    why_now_stat_3_label: 'Est\u00e1n activamente gobernadas',

    // Platform
    platform_title: 'Una Plataforma. Gobernanza Completa.',
    platform_subtitle: 'Del descubrimiento a la contenci\u00f3n en un solo control plane.',
    platform_features: [
      { title: 'Descubrimiento', desc: 'Descubra autom\u00e1ticamente cada identidad no humana en IdPs, cloud y frameworks de agentes.' },
      { title: 'Grafo de Permisos', desc: 'Mapee Agente a Credencial a Herramienta a Recurso. Entienda el radio de impacto instant\u00e1neamente.' },
      { title: 'Motor de Pol\u00edticas', desc: 'Defina y aplique pol\u00edticas RBAC/ABAC. Detecte violaciones en tiempo real.' },
      { title: 'Kill Switch', desc: 'Deshabilite agentes, revoque credenciales o bloquee herramientas instant\u00e1neamente. Un click, completamente auditado.' },
    ],

    // Deployment
    deployment_title: 'Despliegue Que Cumple Su Est\u00e1ndar de Seguridad',
    deployment_subtitle: 'Elija el modelo que se ajuste a sus requisitos de compliance y soberan\u00eda de datos.',
    deployment_modes: [
      { title: 'SaaS', desc: 'Infraestructura gestionada. Conectores read-only. Datos cifrados en reposo.' },
      { title: 'VPC del Cliente', desc: 'Despliegue en su cloud. Los datos nunca salen de su per\u00edmetro.' },
      { title: 'Air-Gapped', desc: 'Aislamiento total. Sin conexiones externas. Actualizaciones offline.' },
    ],

    // Security
    security_title: 'Arquitectura de Seguridad',
    security_subtitle: 'Construido para las organizaciones m\u00e1s exigentes en seguridad',
    security_principles: [
      { title: 'Read-Only Primero', desc: 'El despliegue por defecto usa acceso API de solo lectura. Sin permisos de escritura a su infraestructura.' },
      { title: 'Despliegue Gestionado por el Cliente', desc: 'Despliegue en su propio VPC o on-premise. Soberan\u00eda y control total de datos.' },
      { title: 'Artefactos Firmados', desc: 'Todos los binarios e im\u00e1genes de contenedor est\u00e1n firmados. SBOM y atestaci\u00f3n de procedencia incluidos.' },
      { title: 'Menor Privilegio', desc: 'Permisos m\u00ednimos por dise\u00f1o. Sin acceso admin amplio requerido para operar.' },
      { title: 'Aislamiento de Tenant', desc: 'Segregaci\u00f3n completa de datos entre tenants. Acceso cross-tenant arquitecturalmente imposible.' },
      { title: 'Auditabilidad Total', desc: 'Cada acci\u00f3n registrada de forma inmutable. Exporte audit trails para revisi\u00f3n de compliance.' },
    ],

    // Built For
    built_for_title: 'Construido Para L\u00edderes de Seguridad',
    built_for_tabs: {
      ciso: 'CISO',
      identity: 'Seguridad de Identidad',
      cloud: 'Seguridad Cloud',
      platform: 'Seguridad de Plataforma',
    },
    built_for_ciso_title: 'Para el CISO',
    built_for_ciso_desc: 'Visibilidad lista para el board sobre la superficie de ataque de identidades no humanas. Dashboard ejecutivo con posture score, tendencias de riesgo y estado de incidentes.',
    built_for_ciso_items: ['Tendencia de postura de seguridad', 'Cuantificaci\u00f3n de riesgo', 'Reportes de compliance'],
    built_for_ciso_preview: 'Vista Ejecutiva',
    built_for_ciso_metric_1: 'Posture Score',
    built_for_ciso_metric_2: 'Cr\u00edticos',
    built_for_identity_title: 'Para Seguridad de Identidad',
    built_for_identity_desc: 'Extienda la gobernanza IAM a identidades no humanas. Descubra, clasifique y gestione cada cuenta de servicio, bot y agente de IA.',
    built_for_identity_items: ['Clasificaci\u00f3n autom\u00e1tica de NHI', 'Workflow de asignaci\u00f3n de owner', 'Certificaci\u00f3n de acceso'],
    built_for_identity_preview: 'Inventario de Identidades',
    built_for_cloud_title: 'Para Seguridad Cloud',
    built_for_cloud_desc: 'Rastree cuentas de servicio e identidades de workload en AWS, Azure y GCP. Detecte escalaci\u00f3n de privilegios y acceso cross-account.',
    built_for_cloud_items: ['Visibilidad multi-cloud', 'Detecci\u00f3n de privilegios', 'An\u00e1lisis de radio de impacto'],
    built_for_cloud_preview: 'Cobertura Cloud',
    built_for_platform_title: 'Para Seguridad de Plataforma',
    built_for_platform_desc: 'Gobierne frameworks de agentes de IA antes de que se vuelvan ingobernables. Controle el uso de herramientas y mantenga audit trails completos.',
    built_for_platform_items: ['Conectores de frameworks de agentes', 'Workflow de aprobaci\u00f3n de herramientas', 'Policy-as-code'],
    built_for_platform_preview: 'Gobernanza de Agentes',

    // CTA
    cta_title: '\u00bfListo Para Gobernar Sus Identidades No Humanas?',
    cta_subtitle: 'Despliegue Agent Control Plane en su entorno. Piloto guiado, soporte de ingenier\u00eda dedicado, revisi\u00f3n de seguridad completa.',
    cta_pilot: 'Solicitar Acceso al Piloto',
    cta_explore: 'Explorar Plataforma',

    // Footer
    footer_desc: 'Seguridad enterprise para agentes de IA e identidades no humanas.',
    footer_product: 'Producto',
    footer_company: 'Compa\u00f1\u00eda',
    footer_legal: 'Legal',
    footer_rights: 'Todos los derechos reservados.',

    // Pilot Form
    pilot_title: 'Solicitar un Piloto Controlado',
    pilot_subtitle: 'Despliegue Agent Control Plane en su entorno en dos semanas. Nuestro equipo lo guiar\u00e1 a trav\u00e9s de la configuraci\u00f3n, integraci\u00f3n y primer escaneo de descubrimiento.',
    pilot_name: 'Nombre Completo',
    pilot_email: 'Email Corporativo',
    pilot_company: 'Empresa',
    pilot_role: 'Cargo / T\u00edtulo',
    pilot_role_placeholder: 'CISO, VP de Seguridad, Director de IAM...',
    pilot_nhi: 'Identidades No Humanas',
    pilot_nhi_placeholder: 'Cantidad estimada',
    pilot_deployment: 'Despliegue Preferido',
    pilot_deployment_placeholder: 'Elegir modelo de despliegue',
    pilot_stack: 'Stack de Identidad Actual',
    pilot_stack_placeholder: 'ej., Okta + AWS IAM + CyberArk',
    pilot_notes: 'Contexto Adicional',
    pilot_notes_placeholder: 'Casos de uso espec\u00edficos, requisitos de compliance, o preguntas...',
    pilot_submit: 'Enviar Solicitud de Piloto',
    pilot_submitting: 'Enviando...',
    pilot_required: 'Por favor complete todos los campos obligatorios.',
    pilot_error: 'Algo sali\u00f3 mal. Int\u00e9ntelo de nuevo.',
    pilot_optional: 'opcional',
    pilot_consent: 'Al enviar, acepta ser contactado por nuestro equipo respecto a su solicitud de piloto. No compartimos su informaci\u00f3n con terceros.',
    pilot_trust_1: 'Despliegue en su VPC',
    pilot_trust_2: 'Read-only por defecto',
    pilot_trust_3: 'Piloto guiado de 2 semanas',

    // Pilot Confirmation
    pilot_confirm_title: 'Solicitud de Piloto Recibida',
    pilot_confirm_thanks: 'Gracias,',
    pilot_confirm_message: 'Nuestro equipo revisar\u00e1 su solicitud y se comunicar\u00e1 dentro de las 24 horas para coordinar un briefing t\u00e9cnico para',
    pilot_confirm_summary: 'Resumen de Solicitud',
    pilot_confirm_deployment: 'Despliegue',
    pilot_confirm_nhi: 'Escala NHI',
    pilot_confirm_email_sent: 'Confirmaci\u00f3n enviada a',
    pilot_confirm_home: 'Volver al Inicio',
    pilot_confirm_explore: 'Explorar Plataforma',

    // NHI options
    nhi_options: ['Menos de 100', '100 - 500', '500 - 1.000', '1.000 - 5.000', '5.000+', 'A\u00fan no s\u00e9'],
    deployment_options: [
      { value: 'SaaS', label: 'SaaS (Gestionado)' },
      { value: 'Customer VPC', label: 'VPC del Cliente' },
      { value: 'Air-Gapped / On-Premise', label: 'Air-Gapped / On-Premise' },
      { value: 'Not decided yet', label: 'A\u00fan no decidido' },
    ],
  },
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem('acp_lang');
    return saved || 'en';
  });

  const setLanguage = (newLang) => {
    setLang(newLang);
    localStorage.setItem('acp_lang', newLang);
  };

  const t = (key) => translations[lang]?.[key] ?? translations['en']?.[key] ?? key;

  return (
    <LanguageContext.Provider value={{ lang, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
